import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectUserProject } from 'core/userContext/selectors';
import { FormField } from 'ui/formField';
import { WarningBanner } from 'ui/banner';
import { CHECKBOX_STATUS, Checkbox } from 'ui/checkbox';
import { InputText } from 'ui/inputs';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { body2Bold, spacing } from 'ui/theme';
import { DataFeedsModalStateType, useModalContext } from 'utils/contexts/modal';

import { clearDataFeedsError, updateDataFeedsRequest } from '../../actions';
import { useTranslations } from '../../hooks/useTranslations';
import { validateAlias } from '../../utils';
import { DataFeed } from '../../types';

export const ids = {
  inputAlias: 'df-alias-modal-alias-input',
  checkbox: 'df-alias-modal-confirm-checkbox',
  btnSubmit: 'df-alias-modal-submit-btn',
  modal: 'df-alias-modal',
};

export const testIds = {
  inputAlias: ids.inputAlias,
  checkbox: ids.checkbox,
  btnSubmit: ids.btnSubmit,
};

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  row-gap: ${spacing.plusHalf};
`;

const CheckboxLabel = styled.label`
  ${body2Bold};
  cursor: pointer;
  margin: 0;
`;

const CheckboxField = styled.div`
  column-gap: ${spacing.half};
  display: flex;
  position: relative;
  align-items: center;
`;

export const EditAliasModal = () => {
  const { t } = useTranslations();
  const dispatch = useDispatch();
  const activeProject = useSelector(selectUserProject);
  const { modalState, hideModal } = useModalContext<DataFeedsModalStateType>();
  const dataFeed = modalState?.dataFeed as DataFeed;
  /**
   * -- State --
   * aliasText: tracks edits to the alias input, should update to data.alias when data updates
   * isChecked: tracks confirmation status to checkbox
   * open: shadows `isOpen` prop, allows us time to do intermediate work before modal opens
   * showAliasError: controls whether input error is shown for alias
   * showConfirmationError: controls whether checkbox error is shown for the confirmation checkbox
   */
  const [aliasText, setAliasText] = useState<string>(dataFeed.alias || '');
  const [isChecked, setChecked] = useState(false);
  const [showAliasError, toggleAliasError] = useState(false);
  const [showConfirmationError, toggleConfirmationError] = useState(false);

  const hasAliasChanged = aliasText !== dataFeed.alias;

  const primaryAction = (df: DataFeed) => {
    if (activeProject) {
      const updateDataFeedsAction = updateDataFeedsRequest(
        {
          ...df,
          projectId: activeProject.id, // the API requires a project ID to create a feed
        },
        hideModal,
      );
      dispatch(updateDataFeedsAction);
    }
  };

  // we hide the confirmation error if a check selection happens
  useEffect(() => {
    if (isChecked) {
      toggleConfirmationError(false);
    }
  }, [isChecked]);

  // The alias can only contain characters and underscores
  useEffect(() => {
    const aliasPasses = validateAlias(aliasText);
    toggleAliasError(!aliasPasses);
    if (aliasText === dataFeed.alias) {
      toggleConfirmationError(false);
    }
  }, [aliasText]);

  const submit = () => {
    // don't allow the form to be submitting unless a user confirms knowledge of a potentially destructive change
    if (hasAliasChanged && !isChecked) {
      toggleConfirmationError(true);
    } else {
      primaryAction({
        ...dataFeed,
        alias: aliasText,
      });
    }
  };

  // we won't allow a submission if there's no valid change
  const disableSubmitBtn = !hasAliasChanged || showAliasError || showConfirmationError;

  return (
    <Modal
      id={ids.modal}
      isModalOpen={true}
      onClose={() => {
        hideModal();
        dispatch(clearDataFeedsError());
      }}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader>
        {dataFeed.name
          ? t('header.editAliasSpecific', { name: dataFeed.name })
          : t('header.editAlias')}
      </ModalHeader>
      <StyledModalBody>
        <section>
          <WarningBanner Icon={false}>{t('caption.aliasWarning')}</WarningBanner>
        </section>
        <section>
          <FormField
            errorText={t('input.aliasName.errorText')}
            helpText={t('input.aliasName.helpText')}
            inputId={ids.inputAlias}
            isRequired
            labelText={t('input.aliasName.label')}
            hasError={showAliasError}
          >
            <InputText
              data-test={testIds.inputAlias}
              id={ids.inputAlias}
              onChange={setAliasText}
              value={aliasText}
              autoFocus
            />
          </FormField>
          <FormField
            errorText={t('input.aliasCheckbox.errorText')}
            hasError={showConfirmationError}
            inputId={ids.checkbox}
          >
            <CheckboxField>
              <Checkbox
                data-test={testIds.checkbox}
                error={showConfirmationError}
                id={ids.checkbox}
                name={ids.checkbox}
                onClick={() => setChecked(!isChecked)}
                status={isChecked ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
              />
              <CheckboxLabel htmlFor={ids.checkbox} onClick={() => setChecked(!isChecked)}>
                {t('input.aliasCheckbox.label')}
              </CheckboxLabel>
            </CheckboxField>
          </FormField>
        </section>
      </StyledModalBody>
      <ModalFooter cancelLabel={t('button.cancel')}>
        <Button
          data-test={testIds.btnSubmit}
          id={ids.btnSubmit}
          isDisabled={disableSubmitBtn}
          onClick={submit}
          variant={BUTTON_VARIANTS.PRIMARY}
          size={BUTTON_SIZES.LARGE}
        >
          {t('button.save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditAliasModal;
